import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getSocket } from '../../socket';

export default function useDebateSpeaker({ item, meeting, turns, canControl }) {
  const { user: authUser } = useSelector(state => state.auth);
  const [selectedId, setSelectedId] = useState(item.activeDebateTurnId || '');
  const [previewIndex, setPreviewIndex] = useState(null);
  const [states, setStates] = useState({});
  const pending = useRef(Promise.resolve());
  const revision = useRef(0);
  const meetingId = meeting?.meetingDetails?.id || meeting?.meetingDetails?._id || meeting?._id || item.meeting;
  const agendaItemId = item.agendaItemId;
  const updateTimerState = payload => {
    if (!payload.segmentId) return;
    setStates(current => ({ ...current, [payload.segmentId]: payload }));
    setSelectedId(current => payload.isPlaying ? payload.segmentId : current || payload.segmentId);
  };
  useEffect(() => {
    setSelectedId(item.activeDebateTurnId || '');
    setPreviewIndex(null);
  }, [agendaItemId, item.activeDebateTurnId]);
  useEffect(() => {
    setStates({});
    const socket = getSocket();
    const matches = payload => String(payload?.meetingId) === String(meetingId)
      && String(payload?.agendaItemId) === String(agendaItemId);
    const onSelection = payload => {
      if (!matches(payload)) return;
      revision.current += 1;
      setSelectedId(payload.turnId);
      setPreviewIndex(null);
    };
    const onState = payload => {
      if (!matches(payload) || !payload.segmentId) return;
      setStates(current => ({ ...current, [payload.segmentId]: payload }));
      setSelectedId(current => payload.isPlaying ? payload.segmentId : current || payload.segmentId);
    };
    const onTick = payload => onState({ ...payload, isPlaying: true });
    const onReset = payload => onState({ ...payload, time: 0, isPlaying: false });
    socket.on('meeting:debate-speaker', onSelection);
    socket.on('meeting:timer-state', onState);
    socket.on('meeting:timer-tick', onTick);
    socket.on('meeting:timer-reset', onReset);
    if (meetingId) socket.emit('meeting:join', String(meetingId));
    return () => {
      socket.off('meeting:debate-speaker', onSelection);
      socket.off('meeting:timer-state', onState);
      socket.off('meeting:timer-tick', onTick);
      socket.off('meeting:timer-reset', onReset);
    };
  }, [meetingId, agendaItemId]);

  const index = !canControl && previewIndex !== null
    ? Math.min(previewIndex, Math.max(0, turns.length - 1))
    : Math.max(0, turns.findIndex(turn => turn.id === selectedId));
  const turn = turns[index];
  const running = turns.some(entry => states[entry.id]?.isPlaying);
  const select = nextIndex => {
    const next = turns[nextIndex];
    if (!next || running) return;
    // Non-managers retain the modal's existing local preview navigation.
    if (!canControl) {
      setPreviewIndex(nextIndex);
      return;
    }
    const previousId = turn?.id || '';
    const requestRevision = ++revision.current;
    setSelectedId(next.id);
    pending.current = pending.current.then(async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_URL}/timer/${agendaItemId}/speaker`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authUser?.token || ''}` },
          body: JSON.stringify({ turnId: next.id }),
        });
        if (!response.ok) throw new Error('Unable to change the current speaker. Please try again.');
      } catch (error) {
        if (revision.current === requestRevision) setSelectedId(previousId);
        toast.error(error.message, { position: 'bottom-left' });
      }
    });
  };
  return { turn, index, select, running, liveState: states[turn?.id] || null, updateTimerState };
}
